(() => {
  const doc = document;
  const qs = (selector, root = doc) => root.querySelector(selector);
  const qsa = (selector, root = doc) => Array.from(root.querySelectorAll(selector));
  const clamp = (value, min, max) => Math.min(max, Math.max(min, value));
  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  const marqueeRoots = new Set();
  let marqueeFrame = 0;
  let marqueeLast = performance.now();

  function initMarquees(scope = doc) {
    qsa('[data-pk-marquee-root]', scope).forEach((root) => {
      if (root.dataset.pkMarqueeReady === 'true') return;
      root.dataset.pkMarqueeReady = 'true';
      const track = qs('[data-pk-marquee-track]', root);
      const viewport = qs('[data-pk-marquee-viewport]', root);
      if (!track || !viewport) return;
      root._pkMarquee = { track, viewport, x: 0, speed: Number(root.dataset.pkMarqueeSpeed || 180), target: 1, current: 1 };
      const pause = root.dataset.pkMarqueePause === 'true';
      if (pause) {
        viewport.addEventListener('mouseenter', () => { root._pkMarquee.target = 0; });
        viewport.addEventListener('mouseleave', () => { root._pkMarquee.target = 1; });
        viewport.addEventListener('focusin', () => { root._pkMarquee.target = 0; });
        viewport.addEventListener('focusout', () => { root._pkMarquee.target = 1; });
      }
      marqueeRoots.add(root);
    });
    if (marqueeRoots.size && !marqueeFrame) {
      marqueeLast = performance.now();
      marqueeFrame = requestAnimationFrame(tickMarquees);
    }
  }

  function tickMarquees(now) {
    const dt = Math.min(0.05, Math.max(0, (now - marqueeLast) / 1000));
    marqueeLast = now;
    marqueeRoots.forEach((root) => {
      const state = root._pkMarquee;
      if (!state || !state.track?.isConnected) { marqueeRoots.delete(root); return; }
      const half = state.track.scrollWidth / 2;
      if (!half) return;
      const ease = 1 - Math.exp(-dt * 7);
      state.current += (state.target - state.current) * ease;
      if (!reducedMotion.matches) state.x += state.speed * state.current * dt;
      while (state.x >= half) state.x -= half;
      state.track.style.transform = `translate3d(${(-half + state.x).toFixed(2)}px,0,0)`;
    });
    marqueeFrame = marqueeRoots.size ? requestAnimationFrame(tickMarquees) : 0;
  }

  let headerLastY = window.scrollY;
  let headerFrame = 0;
  function updateHeader() {
    headerFrame = 0;
    const header = qs('[data-pk-header]');
    if (!header) return;
    const y = window.scrollY;
    const delta = y - headerLastY;
    header.classList.toggle('is-scrolled', y > 10);
    if (Math.abs(delta) > 4) {
      header.classList.toggle('is-hidden', delta > 0 && y > 120 && !header.matches(':hover') && !header.matches(':focus-within'));
      headerLastY = y;
    }
  }
  function requestHeaderUpdate() {
    if (!headerFrame) headerFrame = requestAnimationFrame(updateHeader);
  }

  const storyRoots = new Set();
  let storyFrame = 0;

  function ensureMobileStoryPortal(root) {
    if (root._pkStoryPortal?.isConnected) return root._pkStoryPortal;
    const copies = qsa('[data-pk-story-copy]', root);
    if (!copies.length || !doc.body) return null;

    /* v18 mobile-only text portal.
       v16's product motion was reliable, but its whole pinned canvas had to be
       translated every frame to cancel page scrolling. That made the type
       shimmer because JS always runs just after the compositor has scrolled.
       Keep v16's proven product plane exactly as-is and render ONLY the copy in
       a fixed body-level portal. A body child is not affected by Tinker's
       transformed/overflow section ancestors, which is what broke the v17
       position:fixed attempt inside the section. */
    const portal = doc.createElement('div');
    portal.className = 'pk-scroll-story__portal';
    portal.setAttribute('aria-hidden', 'true');
    copies.forEach((copy, index) => {
      const clone = copy.cloneNode(true);
      clone.removeAttribute('data-pk-story-copy');
      clone.dataset.pkStoryPortalCopy = String(index);
      clone.classList.toggle('is-active', index === 0);
      clone.style.opacity = index === 0 ? '1' : '0';
      clone.style.visibility = index === 0 ? 'visible' : 'hidden';
      clone.style.pointerEvents = 'none';
      portal.appendChild(clone);
    });
    doc.body.appendChild(portal);
    root._pkStoryPortal = portal;
    return portal;
  }

  function setStoryPortalActive(root, active) {
    const portal = ensureMobileStoryPortal(root);
    if (!portal) return;
    qsa('[data-pk-story-portal-copy]', portal).forEach((copy, index) => {
      const on = index === active;
      copy.classList.toggle('is-active', on);
      copy.setAttribute('aria-hidden', on ? 'false' : 'true');
      copy.style.opacity = on ? '1' : '0';
      copy.style.visibility = on ? 'visible' : 'hidden';
    });
  }

  function setStoryActive(root, requestedIndex) {
    const copies = qsa('[data-pk-story-copy]', root);
    if (!copies.length) return;
    const active = clamp(Number(requestedIndex) || 0, 0, copies.length - 1);
    copies.forEach((copy, index) => {
      const on = index === active;
      copy.classList.toggle('is-active', on);
      copy.setAttribute('aria-hidden', on ? 'false' : 'true');
      /* Inline state is intentional: it makes the copy switch reliable inside
         Shopify's preview/editor even if theme CSS is re-injected after a
         section reload. CSS still owns the 175ms crossfade. */
      copy.style.opacity = on ? '1' : '0';
      copy.style.visibility = on ? 'visible' : 'hidden';
      copy.style.pointerEvents = 'none';
    });
    root.dataset.pkActiveStory = String(active);
    if (window.matchMedia('(max-width: 1023px)').matches) setStoryPortalActive(root, active);
  }

  function updateScrollStory(root) {
    const scenes = qsa('[data-pk-story-scene]', root);
    if (!scenes.length) return;
    const vh = Math.max(1, window.innerHeight || doc.documentElement.clientHeight || 1);
    const mobile = window.matchMedia('(max-width: 767px)').matches;
    const tablet = window.matchMedia('(min-width: 768px) and (max-width: 1023px)').matches;

    if (mobile || tablet) {
      /* Phone and tablet deliberately use their own reliable scroll plane.
         Desktop >=1024px never enters this branch and keeps the already-working
         Oak-style native sticky implementation. Tablet previously fell into the
         desktop branch while tablet CSS changed the scene geometry; that mixed
         system is why 768–1023px could stall after the first scene. */
      const rect = root.getBoundingClientRect();
      const rootHeight = Math.max(vh, root.offsetHeight || rect.height || vh);
      const scrollable = Math.max(1, rootHeight - vh);
      const scrolled = clamp(-rect.top, 0, scrollable);
      const pinProperty = mobile ? '--pk-mobile-pin-y' : '--pk-tablet-pin-y';
      root.style.setProperty(pinProperty, `${scrolled.toFixed(2)}px`);

      /* A body-level portal keeps the typography compositor-stable. The media
         uses the translated in-section plane, so neither phone nor tablet relies
         on position:sticky inside Tinker's responsive wrappers. */
      const portal = ensureMobileStoryPortal(root);
      if (portal) {
        const portalVisible = rect.top <= 0 && rect.bottom > vh;
        portal.classList.toggle('is-visible', portalVisible);
        portal.classList.toggle('is-tablet', tablet);
        portal.setAttribute('aria-hidden', portalVisible ? 'false' : 'true');
      }

      const count = scenes.length;
      const totalUnits = Math.max(1, count);
      const storyUnits = (scrolled / scrollable) * totalUnits;
      const travelDuration = tablet ? 1.10 : 1.08;
      const stride = count > 1 ? (totalUnits - travelDuration) / (count - 1) : 1;
      const startFactor = tablet ? .82 : .88;
      const endFactor = tablet ? -.90 : -.92;
      let active = 0;
      let nearest = Infinity;

      scenes.forEach((scene, index) => {
        const object = qs(`[data-pk-story-mobile-object="${index}"]`, root);
        if (!object) return;

        const start = index * stride;
        const phase = (storyUnits - start) / travelDuration;
        const travel = clamp(phase, 0, 1);
        const editorOffset = parseFloat(getComputedStyle(object).getPropertyValue('--pk-mobile-y')) || 0;
        const editorPx = (editorOffset / 100) * vh;
        const startY = startFactor * vh;
        const endY = endFactor * vh;
        const y = startY + ((endY - startY) * travel) + editorPx;
        object.style.setProperty('transform', `translate(-50%,-50%) translate3d(0,${y.toFixed(2)}px,0)`, 'important');

        const visible = phase > -.16 && phase < 1.16;
        object.classList.toggle('is-visible', visible);
        if (visible) {
          const distance = Math.abs(y);
          if (distance < nearest) { nearest = distance; active = index; }
        }
      });

      setStoryActive(root, active);
      return;
    }

    /* Desktop behavior deliberately preserved exactly as before. */
    if (root._pkStoryPortal) {
      root._pkStoryPortal.classList.remove('is-visible');
      root._pkStoryPortal.setAttribute('aria-hidden', 'true');
    }
    let active = 0;
    let nearest = Infinity;
    scenes.forEach((scene, index) => {
      const rect = scene.getBoundingClientRect();
      const center = rect.top + rect.height / 2;
      const dist = Math.abs(center - vh / 2);
      if (dist < nearest) { nearest = dist; active = index; }
      const media = qs('[data-pk-story-media]', scene);
      if (media) {
        const progress = clamp((vh - rect.top) / (vh + rect.height), 0, 1);
        const parallax = reducedMotion.matches ? 0 : (progress - .5) * -54;
        media.style.setProperty('--pk-story-parallax', `${parallax.toFixed(2)}px`);
      }
    });
    setStoryActive(root, active);
  }

  function updateAllStories(){ storyFrame = 0; storyRoots.forEach(updateScrollStory); }
  function requestStories(){ if (!storyFrame) storyFrame = requestAnimationFrame(updateAllStories); }

  function initScrollStories(scope = doc){
    qsa('[data-pk-scroll-story]', scope).forEach((root) => {
      if (root.dataset.pkScrollStoryReady === 'true') return;
      root.dataset.pkScrollStoryReady = 'true';
      const scenes = qsa('[data-pk-story-scene]', root);
      storyRoots.add(root);
      setStoryActive(root, 0);
      updateScrollStory(root);

      /* Primary copy switcher: a narrow horizontal band through the middle
         of the viewport. When a physical media scene crosses that band its
         matching sticky copy becomes active. This mirrors the Scrolling
         Cards interaction and does not depend on synthetic animation time. */
      if ('IntersectionObserver' in window && scenes.length && window.innerWidth >= 1024) {
        const observer = new IntersectionObserver((entries) => {
          if (window.innerWidth < 1024) return;
          const intersecting = entries.filter((entry) => entry.isIntersecting);
          if (!intersecting.length) return;
          const viewportCenter = (window.innerHeight || 1) / 2;
          intersecting.sort((a, b) => {
            const ac = a.boundingClientRect.top + a.boundingClientRect.height / 2;
            const bc = b.boundingClientRect.top + b.boundingClientRect.height / 2;
            return Math.abs(ac - viewportCenter) - Math.abs(bc - viewportCenter);
          });
          const index = scenes.indexOf(intersecting[0].target);
          if (index >= 0) setStoryActive(root, index);
        }, { root: null, rootMargin: '-43% 0px -43% 0px', threshold: 0 });
        scenes.forEach((scene) => observer.observe(scene));
        root._pkStoryObserver = observer;
      }
    });
  }

  function gapOf(track) {
    const styles = getComputedStyle(track);
    return parseFloat(styles.columnGap || styles.gap || '0') || 0;
  }

  function setBestsellerTab(tab) {
    const root = tab.closest('[data-pk-tabs]');
    if (!root) return;
    qsa('[data-pk-tab]', root).forEach((button) => {
      const active = button === tab;
      button.setAttribute('aria-selected', active ? 'true' : 'false');
      button.tabIndex = active ? 0 : -1;
    });
    qsa('[data-pk-panel]', root).forEach((panel) => {
      panel.hidden = panel.dataset.pkPanel !== tab.dataset.pkTab;
    });
  }

  function initTabs(scope = doc) {
    qsa('[data-pk-tabs]', scope).forEach((root) => {
      if (root.dataset.pkTabsReady === 'true') return;
      root.dataset.pkTabsReady = 'true';
      const tabs = qsa('[data-pk-tab]', root);
      tabs.forEach((tab, index) => {
        tab.tabIndex = index === 0 ? 0 : -1;
        tab.addEventListener('click', () => setBestsellerTab(tab));
        tab.addEventListener('keydown', (event) => {
          if (!['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return;
          event.preventDefault();
          let next = tabs.indexOf(tab);
          if (event.key === 'ArrowLeft') next = (next - 1 + tabs.length) % tabs.length;
          if (event.key === 'ArrowRight') next = (next + 1) % tabs.length;
          if (event.key === 'Home') next = 0;
          if (event.key === 'End') next = tabs.length - 1;
          tabs[next]?.focus();
          setBestsellerTab(tabs[next]);
        });
      });
    });
  }

  function initCarousel(root) {
    if (!root || root.dataset.pkCarouselReady === 'true') return;
    const track = qs('[data-pk-track]', root);
    if (!track) return;
    root.dataset.pkCarouselReady = 'true';

    const items = () => qsa('[data-pk-item]', track);
    const prev = qs('[data-pk-arrow="prev"]', root);
    const next = qs('[data-pk-arrow="next"]', root);
    const dots = qs('[data-pk-dots]', root);
    let pageSize = 1;
    let step = 0;
    let pageCount = 1;
    let scrollTimer = 0;

    const geometry = () => {
      const first = items()[0];
      if (!first) return;
      const gap = gapOf(track);
      const itemWidth = first.getBoundingClientRect().width;
      if (!itemWidth) return;
      pageSize = Math.max(1, Math.floor((track.clientWidth + gap) / (itemWidth + gap)));
      step = pageSize * (itemWidth + gap);
      pageCount = Math.max(1, Math.ceil(items().length / pageSize));
    };

    const currentPage = () => {
      if (pageCount <= 1 || step <= 0) return 0;
      return clamp(Math.round(track.scrollLeft / step), 0, pageCount - 1);
    };

    const updateControls = () => {
      geometry();
      const page = currentPage();
      if (prev) {
        const disabled = page <= 0;
        prev.disabled = disabled;
        prev.setAttribute('aria-disabled', disabled ? 'true' : 'false');
      }
      if (next) {
        const disabled = page >= pageCount - 1;
        next.disabled = disabled;
        next.setAttribute('aria-disabled', disabled ? 'true' : 'false');
      }
      if (dots) {
        qsa('.pk-carousel-dot', dots).forEach((dot, index) => {
          dot.classList.toggle('is-active', index === page);
        });
      }
      items().forEach((item, index) => {
        const active = index >= page * pageSize && index < (page + 1) * pageSize;
        item.classList.toggle('is-active', active);
      });
    };

    const buildDots = () => {
      if (!dots) return;
      geometry();
      dots.replaceChildren();
      for (let index = 0; index < pageCount; index += 1) {
        const dot = doc.createElement('span');
        dot.className = `pk-carousel-dot${index === 0 ? ' is-active' : ''}`;
        dots.appendChild(dot);
      }
    };

    const move = (direction) => {
      geometry();
      if (!step) return;
      const page = clamp(currentPage() + direction, 0, pageCount - 1);
      track.scrollTo({ left: page * step, behavior: reducedMotion.matches ? 'auto' : 'smooth' });
    };

    prev?.addEventListener('click', () => move(-1));
    next?.addEventListener('click', () => move(1));
    track.addEventListener('scroll', () => {
      window.clearTimeout(scrollTimer);
      scrollTimer = window.setTimeout(updateControls, 70);
    }, { passive: true });

    const resizeObserver = new ResizeObserver(() => {
      buildDots();
      updateControls();
    });
    resizeObserver.observe(track);
    items().forEach((item) => resizeObserver.observe(item));
    root._pkResizeObserver = resizeObserver;

    buildDots();
    updateControls();
  }

  function initCarousels(scope = doc) {
    qsa('[data-pk-carousel]', scope).forEach(initCarousel);
  }

  function initStory(scope = doc) {
    qsa('[data-pk-story]', scope).forEach((root) => {
      if (root.dataset.pkStoryReady === 'true') return;
      root.dataset.pkStoryReady = 'true';
      const panels = qsa('[data-pk-story-panel]', root);
      const images = qsa('[data-pk-story-image]', root);
      const setImage = (index) => {
        images.forEach((image, imageIndex) => image.classList.toggle('is-active', imageIndex === index));
      };
      const setPanel = (target) => {
        const collapse = target.classList.contains('is-active');
        const targetIndex = Number(target.dataset.pkStoryPanelIndex || 0);
        if (!collapse) setImage(targetIndex);
        panels.forEach((panel) => {
          const active = !collapse && panel === target;
          panel.classList.toggle('is-active', active);
          panel.setAttribute('aria-expanded', active ? 'true' : 'false');
        });
      };
      panels.forEach((panel) => panel.addEventListener('click', () => setPanel(panel)));
    });
  }

  const scrollSliders = new Set();
  let scrollSliderFrame = 0;

  function smoothstep(edge0, edge1, value) {
    if (edge0 === edge1) return value < edge0 ? 0 : 1;
    const x = clamp((value - edge0) / (edge1 - edge0), 0, 1);
    return x * x * (3 - 2 * x);
  }

  function updateScrollSlider(root) {
    const slides = qsa('[data-pk-scroll-slide]', root);
    if (!slides.length) return;

    const viewport = Math.max(1, window.innerHeight || doc.documentElement.clientHeight || 1);
    const rect = root.getBoundingClientRect();
    const scrollRange = Math.max(1, root.offsetHeight - viewport);
    const scrolled = clamp(-rect.top, 0, scrollRange);
    const progress = clamp(scrolled / scrollRange, 0, 1);
    const count = slides.length;
    const lastIndex = count - 1;

    /* v7: deterministic Oak-style scroll story.
       The section is one sticky viewport. Each scene owns one equal portion of
       the scroll range. The active product travels continuously from below the
       viewport to above it while the copy stays fixed in front. At the last
       10% of a scene, copy crossfades to the next scene. We deliberately do not
       hide images with visibility: hidden; their Y position itself keeps them
       off-canvas. This avoids the blank-image failure seen in Shopify preview. */
    const timeline = progress * count;
    let activeIndex = Math.floor(timeline);
    if (activeIndex > lastIndex) activeIndex = lastIndex;
    const within = activeIndex === lastIndex && progress >= 1 ? 1 : clamp(timeline - activeIndex, 0, 1);
    const mobile = window.innerWidth <= 767;
    const vh = viewport;

    slides.forEach((slide, index) => {
      const image = qs('[data-pk-scroll-image]', slide);
      const copy = qs('[data-pk-scroll-copy]', slide);
      const relative = index - activeIndex;

      let imageProgress;
      if (relative === 0) imageProgress = within;
      else if (relative < 0) imageProgress = 1;
      else imageProgress = 0;

      if (image) {
        const offsetName = mobile ? '--pk-m-y-offset' : '--pk-y-offset';
        const editorYOffset = parseFloat(getComputedStyle(image).getPropertyValue(offsetName)) || 0;
        const editorPx = (editorYOffset / 100) * vh;

        /* At 0 the object's centre is 70vh below the viewport centre, so its
           leading edge is already near the bottom edge. At 1 it is 105vh above
           centre, guaranteeing the full object exits before the next scene. */
        const startY = 0.70 * vh;
        const endY = -1.05 * vh;
        const y = startY + ((endY - startY) * imageProgress) + editorPx;
        image.style.transform = `translate(-50%, -50%) translate3d(0, ${y.toFixed(2)}px, 0)`;

        /* Keep current and adjacent scene images paintable so handoffs never
           flash blank. Off-stage images are physically outside the viewport. */
        const shouldPaint = Math.abs(relative) <= 1 || (index === lastIndex && activeIndex === lastIndex);
        image.style.opacity = shouldPaint ? '1' : '0';
        image.style.visibility = 'visible';
      }

      if (copy) {
        let opacity = index === activeIndex ? 1 : 0;
        if (!reducedMotion.matches && activeIndex < lastIndex && within > 0.88) {
          const seam = smoothstep(0.88, 1, within);
          if (index === activeIndex) opacity = 1 - seam;
          if (index === activeIndex + 1) opacity = seam;
        }
        copy.style.setProperty('--pk-copy-opacity', String(opacity));
      }

      const accessible = index === activeIndex || (!reducedMotion.matches && activeIndex < lastIndex && within > 0.94 && index === activeIndex + 1);
      slide.setAttribute('aria-hidden', accessible ? 'false' : 'true');
    });

    root.dataset.pkActiveSlide = String(activeIndex);
    root.style.setProperty('--pk-slider-progress', String(progress));
  }

  function updateAllScrollSliders() {
    scrollSliderFrame = 0;
    scrollSliders.forEach(updateScrollSlider);
  }

  function requestScrollSliderUpdate() {
    if (scrollSliderFrame) return;
    scrollSliderFrame = requestAnimationFrame(updateAllScrollSliders);
  }

  function initScrollSliders(scope = doc) {
    qsa('[data-pk-scroll-slider]', scope).forEach((root) => {
      if (root.dataset.pkScrollReady === 'true') return;
      root.dataset.pkScrollReady = 'true';
      scrollSliders.add(root);
      updateScrollSlider(root);
    });
  }

  function initReveal(scope = doc) {
    qsa('[data-pk-reveal]', scope).forEach((el) => {
      if (el.dataset.pkRevealReady === 'true') return;
      el.dataset.pkRevealReady = 'true';
      if (reducedMotion.matches) {
        el.classList.add('is-visible');
        return;
      }
      const observer = new IntersectionObserver((entries, obs) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          entry.target.classList.add('is-visible');
          obs.unobserve(entry.target);
        });
      }, { threshold: 0.12, rootMargin: '0px 0px -6% 0px' });
      observer.observe(el);
      el._pkRevealObserver = observer;
    });
  }

  let footerResizeObserver;
  function initFooterReveal() {
    const footerWrap = qs('.page-wrapper > footer');
    const footer = qs('[data-pk-footer]', footerWrap || doc);
    const main = qs('#MainContent');
    if (!footerWrap || !footer || !main) return;
    const update = () => {
      const desktop = window.innerWidth >= 1024;
      if (!desktop) {
        doc.body.classList.remove('pk-footer-reveal');
        doc.documentElement.style.removeProperty('--pk-footer-reveal-height');
        return;
      }
      const height = Math.ceil(footer.getBoundingClientRect().height);
      doc.documentElement.style.setProperty('--pk-footer-reveal-height', `${height}px`);
      doc.body.classList.add('pk-footer-reveal');
    };
    footerResizeObserver?.disconnect();
    footerResizeObserver = new ResizeObserver(update);
    footerResizeObserver.observe(footer);
    window.addEventListener('resize', update, { passive: true });
    window.addEventListener('orientationchange', update, { passive: true });
    update();
  }

  const editorialCardRoots = new Set();
  let editorialCardFrame = 0;

  function updateEditorialCards(root) {
    const cards = qsa('[data-pk-editorial-card]', root);
    if (!cards.length) return;
    const viewport = Math.max(1, window.innerHeight || doc.documentElement.clientHeight || 1);
    const rect = root.getBoundingClientRect();

    let state = 'in-view';
    if (rect.top > 0) state = 'before';
    else if (rect.bottom <= viewport) state = 'after';
    root.dataset.state = state;

    let activeIndex = state === 'after' ? cards.length - 1 : 0;
    if (state === 'in-view') {
      let nearest = Infinity;
      cards.forEach((card, index) => {
        const media = qs('[data-pk-editorial-media]', card);
        if (!media) return;
        const mediaRect = media.getBoundingClientRect();
        const center = mediaRect.top + (mediaRect.height / 2);
        const distance = Math.abs(center - (viewport / 2));
        if (distance < nearest) {
          nearest = distance;
          activeIndex = index;
        }
      });
    }

    cards.forEach((card, index) => {
      const active = index === activeIndex;
      card.classList.toggle('is-active', active);
      card.setAttribute('aria-hidden', active ? 'false' : 'true');

      /* Oak's preset has media parallax enabled. The object still scrolls in
         normal document flow; this is only a very small inner-media offset. */
      const media = qs('[data-pk-editorial-media]', card);
      const parallax = qs('[data-pk-editorial-parallax]', card);
      if (media && parallax && !reducedMotion.matches && window.innerWidth > 767) {
        const mr = media.getBoundingClientRect();
        const centerDelta = ((mr.top + mr.height / 2) - viewport / 2) / viewport;
        const y = clamp(centerDelta * -24, -24, 24);
        parallax.style.setProperty('--pk-parallax-y', `${y.toFixed(2)}px`);
      } else if (parallax) {
        parallax.style.setProperty('--pk-parallax-y', '0px');
      }
    });

    root.dataset.pkActiveCard = String(activeIndex);
  }

  function updateAllEditorialCards() {
    editorialCardFrame = 0;
    editorialCardRoots.forEach(updateEditorialCards);
  }

  function requestEditorialCardsUpdate() {
    if (editorialCardFrame) return;
    editorialCardFrame = requestAnimationFrame(updateAllEditorialCards);
  }

  function initEditorialCards(scope = doc) {
    qsa('[data-pk-editorial-cards]', scope).forEach((root) => {
      if (root.dataset.pkEditorialReady === 'true') return;
      root.dataset.pkEditorialReady = 'true';
      editorialCardRoots.add(root);
      updateEditorialCards(root);
    });
  }

  function init(scope = doc) {
    initTabs(scope);
    initCarousels(scope);
    initMarquees(scope);
    initScrollStories(scope);
    initStory(scope);
    initScrollSliders(scope);
    initEditorialCards(scope);
    initReveal(scope);
    initFooterReveal();
  }

  doc.addEventListener('DOMContentLoaded', () => init());
  doc.addEventListener('shopify:section:load', (event) => init(event.target));
  doc.addEventListener('shopify:section:unload', (event) => {
    qsa('[data-pk-scroll-slider]', event.target).forEach((root) => scrollSliders.delete(root));
    qsa('[data-pk-scroll-story]', event.target).forEach((root) => {
      root._pkStoryObserver?.disconnect?.();
      root._pkStoryPortal?.remove?.();
      root._pkStoryPortal = null;
      storyRoots.delete(root);
    });
    qsa('[data-pk-marquee-root]', event.target).forEach((root) => marqueeRoots.delete(root));
    qsa('[data-pk-editorial-cards]', event.target).forEach((root) => editorialCardRoots.delete(root));
    qsa('[data-pk-carousel]', event.target).forEach((root) => root._pkResizeObserver?.disconnect());
  });
  doc.addEventListener('shopify:block:select', (event) => {
    const storyScene = event.target?.closest?.('[data-pk-story-scene]');
    if (storyScene) {
      storyScene.scrollIntoView({ behavior: reducedMotion.matches ? 'auto' : 'smooth', block: 'center' });
      return;
    }
    const editorialCard = event.target?.closest?.('[data-pk-editorial-card]');
    if (editorialCard) {
      const media = qs('[data-pk-editorial-media]', editorialCard);
      if (media) {
        const target = window.scrollY + media.getBoundingClientRect().top - ((window.innerHeight - media.getBoundingClientRect().height) / 2);
        window.scrollTo({ top: target, behavior: reducedMotion.matches ? 'auto' : 'smooth' });
      }
      return;
    }
    const slide = event.target?.closest?.('[data-pk-scroll-slide]');
    const root = slide?.closest?.('[data-pk-scroll-slider]');
    if (!slide || !root) return;
    const slides = qsa('[data-pk-scroll-slide]', root);
    const index = slides.indexOf(slide);
    if (index < 0) return;
    const viewport = window.innerHeight || 1;
    const range = Math.max(1, root.offsetHeight - viewport);
    const progress = (index + 0.5) / slides.length;
    const target = window.scrollY + root.getBoundingClientRect().top + (progress * range);
    window.scrollTo({ top: target, behavior: reducedMotion.matches ? 'auto' : 'smooth' });
  });

  window.addEventListener('scroll', requestScrollSliderUpdate, { passive: true });
  window.addEventListener('scroll', requestStories, { passive: true });
  window.addEventListener('scroll', requestHeaderUpdate, { passive: true });
  window.addEventListener('scroll', requestEditorialCardsUpdate, { passive: true });
  window.addEventListener('resize', () => { requestScrollSliderUpdate(); requestEditorialCardsUpdate(); requestStories(); initFooterReveal(); }, { passive: true });
  reducedMotion.addEventListener?.('change', requestScrollSliderUpdate);
  reducedMotion.addEventListener?.('change', requestEditorialCardsUpdate);

  if (doc.readyState !== 'loading') init();
})();

/* v4 product page interactions. */
(() => {
  const money = (cents) => {
    try {
      const currency = window.Shopify?.currency?.active || 'EUR';
      return new Intl.NumberFormat(undefined, { style: 'currency', currency }).format((Number(cents) || 0) / 100);
    } catch (_) {
      return `${((Number(cents) || 0) / 100).toFixed(2)}`;
    }
  };

  function initPdp(root) {
    if (!root || root.dataset.pkPdpReady === 'true') return;
    root.dataset.pkPdpReady = 'true';

    const qty = root.querySelector('[data-pk-qty-input]');
    root.querySelector('[data-pk-qty-minus]')?.addEventListener('click', () => {
      if (!qty) return;
      qty.value = String(Math.max(Number(qty.min || 1), (Number(qty.value) || 1) - 1));
      qty.dispatchEvent(new Event('change', { bubbles: true }));
    });
    root.querySelector('[data-pk-qty-plus]')?.addEventListener('click', () => {
      if (!qty) return;
      qty.value = String((Number(qty.value) || 1) + 1);
      qty.dispatchEvent(new Event('change', { bubbles: true }));
    });

    const variantSelect = root.querySelector('[data-pk-variant-select]');
    const variantInput = root.querySelector('[data-pk-variant-id]');
    const variantsNode = root.querySelector('[data-pk-variants]');
    const price = root.querySelector('[data-pk-product-price]');
    const inventory = root.querySelector('[data-pk-inventory]');
    const add = root.querySelector('[data-pk-main-add]');
    const stickyButton = root.querySelector('[data-pk-sticky-submit]');
    let variants = [];
    try { variants = JSON.parse(variantsNode?.textContent || '[]'); } catch (_) {}

    const updateVariant = () => {
      if (!variantSelect) return;
      const variant = variants.find((item) => String(item.id) === String(variantSelect.value));
      if (!variant) return;
      if (variantInput) variantInput.value = String(variant.id);
      if (price) {
        price.innerHTML = `<span class="pk-pdp__sale">${money(variant.price)}</span>${variant.compare_at_price && variant.compare_at_price > variant.price ? `<span class="pk-pdp__compare">${money(variant.compare_at_price)}</span>` : ''}`;
      }
      if (inventory) {
        const dot = inventory.querySelector('.pk-pdp__inventory-dot');
        dot?.classList.toggle('is-unavailable', !variant.available);
        const text = inventory.querySelector('span:last-child');
        if (text) {
          text.textContent = variant.available
            ? (variant.inventory_management === 'shopify' && Number(variant.inventory_quantity) > 0 ? `There are ${variant.inventory_quantity} products left` : 'In stock')
            : 'Sold out';
        }
      }
      if (add) add.disabled = !variant.available;
      if (stickyButton) {
        stickyButton.disabled = !variant.available;
        stickyButton.textContent = variant.available ? 'Add to cart' : 'Sold out';
      }
      const label = root.querySelector('[data-pk-add-label]');
      if (label) label.textContent = variant.available ? 'Add to cart' : 'Sold out';
    };
    variantSelect?.addEventListener('change', updateVariant);

    const mediaItems = Array.from(root.querySelectorAll('[data-pk-media-index]'));
    const mediaThumbs = Array.from(root.querySelectorAll('[data-pk-pdp-thumb]'));
    const galleryTrack = root.querySelector('[data-pk-pdp-gallery-track]');
    const mobileGalleryDots = Array.from(root.querySelectorAll('[data-pk-pdp-gallery-dot]'));
    const mobileGalleryPrev = root.querySelector('[data-pk-pdp-gallery-prev]');
    const mobileGalleryNext = root.querySelector('[data-pk-pdp-gallery-next]');
    const isMobileGallery = () => window.matchMedia('(max-width: 767px)').matches;

    const setActiveMedia = (index) => {
      const safe = clamp(Number(index) || 0, 0, Math.max(0, mediaItems.length - 1));
      mediaThumbs.forEach((thumb, thumbIndex) => thumb.classList.toggle('is-active', thumbIndex === safe));
      mobileGalleryDots.forEach((dot, dotIndex) => dot.classList.toggle('is-active', dotIndex === safe));
      root.dataset.pkActiveMedia = String(safe);
    };

    const scrollGalleryTo = (index, behavior = 'smooth') => {
      if (!galleryTrack || !mediaItems.length) return;
      const safe = clamp(Number(index) || 0, 0, mediaItems.length - 1);
      const target = mediaItems[safe];
      if (isMobileGallery()) {
        galleryTrack.scrollTo({ left: target.offsetLeft, behavior });
      } else {
        target.scrollIntoView({ behavior, block: 'start' });
      }
      setActiveMedia(safe);
    };

    mediaThumbs.forEach((thumb) => {
      thumb.addEventListener('click', (event) => {
        const index = Number(thumb.dataset.pkThumbIndex || 0);
        event.preventDefault();
        scrollGalleryTo(index, reducedMotion.matches ? 'auto' : 'smooth');
      });
    });

    mobileGalleryPrev?.addEventListener('click', () => {
      const current = Number(root.dataset.pkActiveMedia || 0);
      scrollGalleryTo(current - 1, reducedMotion.matches ? 'auto' : 'smooth');
    });
    mobileGalleryNext?.addEventListener('click', () => {
      const current = Number(root.dataset.pkActiveMedia || 0);
      scrollGalleryTo(current + 1, reducedMotion.matches ? 'auto' : 'smooth');
    });

    if (galleryTrack && mediaItems.length) {
      let galleryFrame = 0;
      const syncMobileGallery = () => {
        galleryFrame = 0;
        if (!isMobileGallery()) return;
        const center = galleryTrack.scrollLeft + galleryTrack.clientWidth / 2;
        let activeIndex = 0;
        let nearest = Infinity;
        mediaItems.forEach((media, index) => {
          const mediaCenter = media.offsetLeft + media.offsetWidth / 2;
          const distance = Math.abs(mediaCenter - center);
          if (distance < nearest) { nearest = distance; activeIndex = index; }
        });
        setActiveMedia(activeIndex);
      };
      galleryTrack.addEventListener('scroll', () => {
        if (!galleryFrame) galleryFrame = requestAnimationFrame(syncMobileGallery);
      }, { passive: true });
      window.addEventListener('resize', syncMobileGallery, { passive: true });
      setActiveMedia(0);
      syncMobileGallery();
    }

    if (mediaItems.length && mediaThumbs.length && 'IntersectionObserver' in window) {
      const observerRoot = isMobileGallery() ? galleryTrack : null;
      const mediaObserver = new IntersectionObserver((entries) => {
        if (isMobileGallery()) return;
        const visible = entries.filter((entry) => entry.isIntersecting).sort((a, b) => b.intersectionRatio - a.intersectionRatio)[0];
        if (!visible) return;
        const index = Number(visible.target.dataset.pkMediaIndex || 0);
        setActiveMedia(index);
      }, observerRoot
        ? { root: observerRoot, threshold: [0.5, 0.75] }
        : { threshold: [0.25, 0.5, 0.75], rootMargin: '-12% 0px -12% 0px' });
      mediaItems.forEach((media) => mediaObserver.observe(media));
      root._pkMediaObserver = mediaObserver;
    }

    const companionWrap = root.querySelector('.pk-pdp__companion-wrap');
    const firstMedia = root.querySelector('.pk-pdp__media');
    if (companionWrap && firstMedia) {
      const companionObserver = new IntersectionObserver((entries) => {
        const entry = entries[0];
        companionWrap.classList.toggle('is-visible', entry.isIntersecting && entry.intersectionRatio > 0.08);
      }, { threshold: [0, 0.08, 0.2] });
      companionObserver.observe(firstMedia);
      root._pkCompanionObserver = companionObserver;
      root.querySelector('[data-pk-companion-close]')?.addEventListener('click', () => {
        companionWrap.classList.add('is-dismissed');
        companionObserver.disconnect();
      });
    }

    const sticky = root.querySelector('[data-pk-sticky-add]');
    if (sticky && add) {
      const setSticky = (visible) => {
        /* Oak keeps the compact purchase bar visible through recommendations and
           even while the footer enters the viewport. */
        sticky.classList.toggle('is-visible', visible);
        sticky.setAttribute('aria-hidden', visible ? 'false' : 'true');
      };
      const observer = new IntersectionObserver((entries) => {
        const entry = entries[0];
        setSticky(!entry.isIntersecting && entry.boundingClientRect.top < 0);
      }, { threshold: 0.05 });
      observer.observe(add);
      root._pkPdpObserver = observer;
      window.addEventListener('scroll', () => {
        if (sticky.classList.contains('is-visible')) setSticky(true);
      }, { passive: true });
      stickyButton?.addEventListener('click', () => {
        const form = add.closest('form');
        if (form && !add.disabled) form.requestSubmit(add);
      });
    }

    const popup = root.querySelector('[data-pk-pdp-popup]');
    if (popup) {
      const storageKey = `pk-pdp-popup-${root.dataset.sectionId || 'product'}`;
      const closePopup = () => {
        popup.classList.remove('is-open');
        popup.setAttribute('aria-hidden', 'true');
        document.body.classList.remove('pk-popup-open');
        try { localStorage.setItem(storageKey, String(Date.now())); } catch (_) {}
      };
      const openPopup = () => {
        popup.classList.add('is-open');
        popup.setAttribute('aria-hidden', 'false');
        document.body.classList.add('pk-popup-open');
        popup.querySelector('input[type="email"]')?.focus({ preventScroll: true });
      };
      popup.querySelectorAll('[data-pk-popup-close]').forEach((button) => button.addEventListener('click', closePopup));
      popup.addEventListener('keydown', (event) => {
        if (event.key === 'Escape') closePopup();
      });
      let recentlySeen = false;
      try {
        const last = Number(localStorage.getItem(storageKey) || 0);
        recentlySeen = Date.now() - last < (7 * 24 * 60 * 60 * 1000);
      } catch (_) {}
      if (!recentlySeen && !window.Shopify?.designMode) {
        const delay = Math.max(0, Number(popup.dataset.delay) || 2500);
        window.setTimeout(openPopup, delay);
      }
    }
  }

  async function submitAjaxAdd(form) {
    if (!form || form.dataset.pkAjaxReady === 'busy') return;
    form.dataset.pkAjaxReady = 'busy';
    const button = form.querySelector('button[type="submit"]');
    const oldLabel = button?.textContent;
    if (button) button.disabled = true;
    try {
      const response = await fetch(`${window.Shopify?.routes?.root || '/'}cart/add.js`, {
        method: 'POST',
        headers: { Accept: 'application/json' },
        body: new FormData(form),
      });
      if (!response.ok) throw new Error('Unable to add item');
      const cart = await fetch(`${window.Shopify?.routes?.root || '/'}cart.js`, { headers: { Accept: 'application/json' } }).then((r) => r.json());
      document.querySelectorAll('.pk-header__cart-count').forEach((count) => {
        count.textContent = String(cart.item_count || 0);
        count.hidden = !cart.item_count;
      });
      if (!document.querySelector('.pk-header__cart-count') && cart.item_count) {
        const cartLink = document.querySelector('.pk-header__cart');
        if (cartLink) {
          const badge = document.createElement('span');
          badge.className = 'pk-header__cart-count';
          badge.textContent = String(cart.item_count);
          cartLink.appendChild(badge);
        }
      }
      /* Let Horizon's standard cart action open the native drawer when available. */
      const openCart = window.Shopify?.actions?.openCart;
      if (typeof openCart === 'function') {
        try { await openCart(); } catch (_) {}
      } else if (typeof openCart?.perform === 'function') {
        try { await openCart.perform(); } catch (_) {}
      } else {
        const drawer = document.querySelector('theme-drawer#cart-drawer');
        if (typeof drawer?.open === 'function') drawer.open();
      }
    } catch (_) {
      form.submit();
    } finally {
      form.dataset.pkAjaxReady = 'true';
      if (button) {
        button.disabled = false;
        if (oldLabel != null) button.textContent = oldLabel;
      }
    }
  }

  function initAjaxAdds(scope = document) {
    scope.querySelectorAll?.('.pk-product-card__quick, .pk-ajax-add').forEach((form) => {
      if (form.dataset.pkAjaxReady) return;
      form.dataset.pkAjaxReady = 'true';
      form.addEventListener('submit', (event) => {
        event.preventDefault();
        submitAjaxAdd(form);
      });
    });
  }

  function init(scope = document) {
    scope.querySelectorAll?.('[data-pk-pdp]').forEach(initPdp);
    initAjaxAdds(scope);
  }
  document.addEventListener('DOMContentLoaded', () => init());
  document.addEventListener('shopify:section:load', (event) => init(event.target));
  if (document.readyState !== 'loading') init();
})();

/* v9 global popovers — newsletter at scroll depth and optional product card. */
(() => {
  const WEEK = 7 * 24 * 60 * 60 * 1000;
  const designMode = Boolean(window.Shopify?.designMode);
  const seenRecently = (key) => {
    try { return Date.now() - Number(localStorage.getItem(key) || 0) < WEEK; } catch (_) { return false; }
  };
  const markSeen = (key) => { try { localStorage.setItem(key, String(Date.now())); } catch (_) {} };

  function initGlobalPopovers(scope = document) {
    scope.querySelectorAll('[data-pk-global-popovers]').forEach((root) => {
      if (root.dataset.pkGlobalReady === 'true') return;
      root.dataset.pkGlobalReady = 'true';

      const modal = root.querySelector('[data-pk-newsletter-modal]');
      if (modal) {
        const key = 'pk-newsletter-weekly-v9';
        const close = () => {
          modal.classList.remove('is-open');
          modal.setAttribute('aria-hidden', 'true');
          document.body.classList.remove('pk-popup-open');
          markSeen(key);
        };
        const open = () => {
          if (designMode || seenRecently(key) || modal.classList.contains('is-open')) return;
          modal.classList.add('is-open');
          modal.setAttribute('aria-hidden', 'false');
          document.body.classList.add('pk-popup-open');
        };
        modal.querySelectorAll('[data-pk-modal-close]').forEach((button) => button.addEventListener('click', close));
        modal.addEventListener('keydown', (event) => { if (event.key === 'Escape') close(); });
        const threshold = Math.min(90, Math.max(10, Number(modal.dataset.scrollTrigger || 50))) / 100;
        const check = () => {
          const max = Math.max(1, document.documentElement.scrollHeight - window.innerHeight);
          if (window.scrollY / max >= threshold) {
            window.removeEventListener('scroll', check);
            open();
          }
        };
        if (!seenRecently(key) && !designMode) {
          window.addEventListener('scroll', check, { passive: true });
          check();
        }
      }

      const productPopover = root.querySelector('[data-pk-product-popover]');
      if (productPopover) {
        const key = 'pk-product-popover-weekly-v9';
        const close = () => {
          productPopover.classList.remove('is-open');
          productPopover.setAttribute('aria-hidden', 'true');
          markSeen(key);
        };
        productPopover.querySelector('[data-pk-product-popover-close]')?.addEventListener('click', close);
        if (!seenRecently(key) && !designMode) {
          const delay = Math.max(1, Number(productPopover.dataset.delay || 1)) * 1000;
          window.setTimeout(() => {
            productPopover.classList.add('is-open');
            productPopover.setAttribute('aria-hidden', 'false');
          }, delay);
        }
      }
    });
  }

  document.addEventListener('DOMContentLoaded', () => initGlobalPopovers());
  document.addEventListener('shopify:section:load', (event) => initGlobalPopovers(event.target));
  if (document.readyState !== 'loading') initGlobalPopovers();
})();

/* v9 Shopify automatic product recommendations. */
(() => {
  async function hydrateRecommendations(root) {
    if (!root || root.dataset.pkRecommendationsLoaded !== undefined || root.dataset.pkRecommendationsBusy === 'true') return;
    const url = root.dataset.url;
    if (!url) return;
    root.dataset.pkRecommendationsBusy = 'true';
    try {
      const html = await fetch(url, { headers: { 'X-Requested-With': 'XMLHttpRequest' } }).then((r) => r.text());
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const fresh = doc.querySelector('[data-pk-recommendations]');
      if (fresh) {
        root.replaceWith(fresh);
        fresh.querySelectorAll('[data-pk-carousel]').forEach((carousel) => {
          /* init happens through the section-level listener on the next frame */
          carousel.dataset.pkCarouselReady = '';
        });
        fresh.dispatchEvent(new CustomEvent('shopify:section:load', { bubbles: true }));
      }
    } catch (_) {
      root.dataset.pkRecommendationsBusy = 'false';
    }
  }
  function init(scope = document) { scope.querySelectorAll('[data-pk-recommendations]').forEach(hydrateRecommendations); }
  document.addEventListener('DOMContentLoaded', () => init());
  document.addEventListener('shopify:section:load', (event) => init(event.target));
  if (document.readyState !== 'loading') init();
})();
